import React from 'react';
import { Brain, Database, Globe, Code } from 'lucide-react';
import { personalInfo } from '../data/mock';

const About = () => {
  const highlights = [
    {
      icon: Brain,
      title: "AI/ML Expertise",
      description: "Specialized in computer vision, NLP, and deep learning with hands-on experience in CNNs, RNNs, GANs, and transformer architectures."
    },
    {
      icon: Code,
      title: "Full-Stack Development", 
      description: "Proficient in building end-to-end applications using React, FastAPI, Django, and modern web technologies."
    },
    {
      icon: Database,
      title: "Data Engineering",
      description: "Experience with data pipelines, preprocessing, feature engineering, and working with large-scale datasets."
    },
    {
      icon: Globe,
      title: "Production Focus",
      description: "Strong track record of deploying ML models and web applications with focus on performance and scalability."
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-[rgb(17,17,19)] to-[rgb(26,28,30)] relative overflow-hidden">
      {/* Glassmorphism background elements */}
      <div className="absolute top-10 right-10 w-40 h-40 rounded-full bg-gradient-to-br from-[rgba(218,255,1,0.1)] to-transparent backdrop-blur-sm opacity-50"></div>
      <div className="absolute bottom-20 left-10 w-32 h-32 rounded-full bg-gradient-to-tl from-[rgba(218,255,1,0.08)] to-transparent backdrop-blur-sm opacity-40"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header with glass effect */}
          <div className="text-center mb-16">
            <div className="inline-block glass-light px-6 py-2 rounded-full mb-6">
              <span className="text-[rgb(218,255,1)] text-sm font-medium tracking-wide">GET TO KNOW ME</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 relative">
              <span className="relative z-10">
                About <span className="text-[rgb(218,255,1)] relative">
                  Me
                  <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-20 blur-xl rounded-lg"></div>
                </span>
              </span>
            </h2>
            <p className="text-xl text-[rgb(161,161,170)] max-w-3xl mx-auto leading-relaxed">
              Passionate about transforming complex data into actionable insights and building scalable AI solutions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Story with glass background */}
            <div className="space-y-8">
              <div className="glass-card rounded-2xl p-8 space-y-6">
                <h3 className="text-2xl font-semibold text-white flex items-center gap-3">
                  <div className="w-2 h-8 bg-[rgb(218,255,1)] rounded-full shadow-[0_0_10px_rgba(218,255,1,0.5)]"></div>
                  My Journey
                </h3>
                <div className="space-y-4 text-[rgb(218,218,218)] leading-relaxed">
                  <p>
                    As a Computer Engineering graduate from IOE Paschimanchal Campus, I've dedicated myself to mastering the intersection of artificial intelligence and software engineering. My journey began with a curiosity about how machines could learn and evolved into building production-ready AI systems.
                  </p>
                  
                  <p>
                    From developing automated football highlight systems to implementing GANs for image synthesis, I've tackled diverse challenges that span computer vision, natural language processing, and full-stack development. Each project has taught me the importance of bridging the gap between research and real-world applications.
                  </p>

                  <p>
                    I believe in the power of continuous learning and have earned multiple certifications from DeepLearning.AI while working on projects that solve real problems. My goal is to contribute to teams building the next generation of AI-powered products.
                  </p>
                </div>
              </div>

              {/* Key Stats with glassmorphism */}
              <div className="grid grid-cols-2 gap-4">
                <div className="glass-card rounded-2xl p-6 text-center hover:glass-card transition-all duration-300 hover:transform hover:scale-105">
                  <div className="text-2xl font-bold text-[rgb(218,255,1)] mb-2 relative">
                    B.E.
                    <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-10 blur-md rounded-lg"></div>
                  </div>
                  <div className="text-sm text-[rgb(161,161,170)]">Computer Engineering</div>
                </div>
                <div className="glass-card rounded-2xl p-6 text-center hover:glass-card transition-all duration-300 hover:transform hover:scale-105">
                  <div className="text-2xl font-bold text-[rgb(218,255,1)] mb-2 relative">
                    Nepal
                    <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-10 blur-md rounded-lg"></div>
                  </div>
                  <div className="text-sm text-[rgb(161,161,170)]">Based in</div>
                </div>
              </div>
            </div>

            {/* Right Column - Highlights with enhanced glassmorphism */}
            <div className="space-y-6">
              {highlights.map((highlight, index) => (
                <div 
                  key={index}
                  className="group glass-card rounded-2xl p-6 hover:glass-card transition-all duration-300 hover:transform hover:translate-y-[-4px] hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] relative overflow-hidden"
                >
                  {/* Top accent glow */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[rgb(218,255,1)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="flex items-start space-x-4 relative z-10">
                    <div className="glass-light w-14 h-14 rounded-2xl flex items-center justify-center text-[rgb(218,255,1)] group-hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300 group-hover:scale-110">
                      <highlight.icon size={24} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white mb-3 group-hover:text-[rgb(218,255,1)] transition-colors duration-300">
                        {highlight.title}
                      </h4>
                      <p className="text-[rgb(161,161,170)] leading-relaxed">
                        {highlight.description}
                      </p>
                    </div>
                  </div>

                  {/* Subtle shimmer effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[rgba(218,255,1,0.05)] to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-out"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;